"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";

type Options = React.ComponentProps<typeof PayPalButtons>;

declare global {
  interface Window {
    __pp_appswitch_resumed?: boolean;
  }
}

export function PayPalButtonsWithResume({
  options,
  isVaultFlow = false,
}: {
  options: Options;
  // The app-switch resume probe resumes an existing charge order. A vault
  // setup token is not a resumable order, so the vault flow skips the probe
  // and renders the buttons directly.
  isVaultFlow?: boolean;
}) {
  const [{ isPending, isResolved, options: sdkOptions }] =
    usePayPalScriptReducer();

  const [ready, setReady] = useState(false);
  const [resumed, setResumed] = useState(false);
  const didResume = useRef(false);

  const namespace = useMemo(() => {
    const configuredNamespace = (sdkOptions as { dataNamespace?: string })
      ?.dataNamespace;

    // Don't fallback to generic "paypal" - use the configured namespace or bail
    if (!configuredNamespace) {
      console.warn("PayPal namespace not configured, skipping resume check");
      return null;
    }

    return configuredNamespace;
  }, [sdkOptions]);

  const {
    fundingSource,
    style,
    createOrder,
    onApprove,
    onError,
    onCancel,
    className,
    disabled,
  } = options;

  useEffect(() => {
    if (
      isVaultFlow || // Vault setup tokens are not resumable orders
      isPending ||
      !isResolved ||
      didResume.current ||
      window.__pp_appswitch_resumed ||
      !namespace // Skip if no namespace
    ) {
      setReady(true);
      return;
    }

    let resumeTimeoutId: ReturnType<typeof setTimeout> | undefined;

    let resumeCancelled = false;

    (async () => {
      try {
        // Access PayPal namespace from window
        const paypalNS = (window as unknown as Record<string, unknown>)?.[
          namespace
        ] as
          | {
              Buttons?: (config: Record<string, unknown>) => {
                hasReturned?: () => boolean;
                resume?: () => Promise<void>;
              };
            }
          | undefined;

        if (!paypalNS?.Buttons) {
          console.warn(`PayPal namespace "${namespace}" not found on window`);
          setReady(true);
          return;
        }

        // Guard callbacks so a zombie resume() that settles after the
        // timeout cannot fire onApprove/onError/onCancel into the
        // recovered UI.
        const guard = (fn: unknown) =>
          typeof fn === "function"
            ? (...args: unknown[]) => {
                if (resumeCancelled) return;
                return (fn as (...a: unknown[]) => unknown)(...args);
              }
            : fn;

        // Create temp buttons instance to check for app-switch resume
        const tempButtons = paypalNS.Buttons({
          appSwitchWhenAvailable: true,
          fundingSource,
          style,
          createOrder: guard(createOrder),
          onApprove: guard(onApprove),
          onError: guard(onError),
          onCancel: guard(onCancel),
        });

        if (
          typeof tempButtons?.hasReturned === "function" &&
          tempButtons.hasReturned()
        ) {
          didResume.current = true;
          window.__pp_appswitch_resumed = true;
          if (typeof tempButtons.resume === "function") {
            const RESUME_TIMEOUT_MS = 10_000;
            const resumePromise = tempButtons.resume();
            // Swallow rejections from the orphaned promise after timeout wins
            resumePromise.catch(() => {});
            await Promise.race([
              resumePromise,
              new Promise<never>((_, reject) => {
                resumeTimeoutId = setTimeout(
                  () => reject(new Error("PayPal resume timed out")),
                  RESUME_TIMEOUT_MS,
                );
              }),
            ]);
            setResumed(true);
          }
        }
      } catch (e) {
        resumeCancelled = true;
        console.error("Resume check failed:", e);
      } finally {
        clearTimeout(resumeTimeoutId);
        setReady(true);
      }
    })();

    return () => {
      resumeCancelled = true;
      clearTimeout(resumeTimeoutId);
    };
  }, [
    isVaultFlow,
    isPending,
    isResolved,
    namespace,
    fundingSource,
    style,
    createOrder,
    onApprove,
    onError,
    onCancel,
  ]);

  if (!ready) {
    return <div className="h-input w-full animate-pulse rounded bg-gray-200" />;
  }

  if (resumed) {
    return (
      <div className="h-input flex w-full items-center justify-center rounded border border-blue-200 bg-blue-50">
        <div className="relative">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
          <div
            className="absolute inset-0 h-5 w-5 animate-spin rounded-full border-2 border-blue-300 border-b-transparent"
            style={{
              animationDirection: "reverse",
              animationDuration: "1.5s",
            }}
          ></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <PayPalButtons
        {...options}
        appSwitchWhenAvailable={true}
        className={className}
        disabled={disabled}
      />
    </div>
  );
}
